import { createAsyncThunk, createSlice, SerializedError, Draft } from '@reduxjs/toolkit';
import { AlgoliaArea, AreaId } from '../../../types/areas';
import { AlertId, AlertModel, AlgoliaAlert } from '../../../types/alerts';
import { TrailsAlertResponse } from '../../../utils/requests/alertRequests';
import { getAlgoliaAlert } from '../../../utils/alertsHelper';
import { AlgoliaObjectType, GroupSearchParams, getTypeFilters, getGroupFilters } from '../../../utils/algoliaHelper';
import { search } from '../../../utils/requests/algoliaRequests';
import { TimedRecord, timedRecordGet, timedRecordSet } from '../../../utils/timedRecord';
import { DEFAULT_CACHE_EXPIRY_MS } from '../../config';
import { createAlert, updateAlert } from '../../thunks/alerts';

export const ALERT_CACHE_EXPIRY_MS = DEFAULT_CACHE_EXPIRY_MS;

export type AreasState = {
  areas: Record<AreaId, AlgoliaArea>;
  started: boolean;
  loading: boolean;
  error: SerializedError;
  alertCache: Record<AreaId, TimedRecord<AlertId, AlgoliaAlert>>;
};

export const initialState: AreasState = {
  areas: {},
  started: false,
  loading: false,
  error: null,
  alertCache: {}
};

export const selectStatus = ({ areas: { started, loading, areas } }: { areas: AreasState }) => ({ started, loading, areas });

export const fetchGroupsAreas = createAsyncThunk('areas/fetchGroupsAreas', async ({ indexId, groups }: GroupSearchParams) => {
  // Prevent searching without any group_ids filter
  if (groups.length === 0) {
    return [];
  }

  const groupFilters = getGroupFilters(groups);
  const typeFilter = getTypeFilters([AlgoliaObjectType.Area]);
  const filters = `${typeFilter} AND ${groupFilters}`;
  const attributesToRetrieve = ['ID', 'name', 'slug', 'city_name', 'state_name', 'country_name'];

  const response = await search<AlgoliaArea>(indexId, '', { attributesToRetrieve, filters, hitsPerPage: 1000 });
  return response.hits;
});

const updateAlertIds = (state: Draft<AreasState>, alert: TrailsAlertResponse) => {
  if (alert.type === AlertModel.AreaAlert) {
    const areaId = alert.objectId;
    if (areaId in state.areas) {
      state.areas[areaId].alert_ids ||= [];
      const alertIds = state.areas[areaId].alert_ids;
      if (alertIds && !alertIds.includes(alert.id)) {
        state.areas[areaId].alert_ids.push(alert.id);
      }
    }
  }
};

const addAlertsFromCache = (state: Draft<AreasState>, area: AlgoliaArea) => {
  if (!(area.ID in state.alertCache)) {
    return;
  }

  state.areas[area.ID].alert_ids ||= [];
  Object.keys(state.alertCache[area.ID]).forEach(alertIdString => {
    const alertId = parseInt(alertIdString);
    const alert = timedRecordGet(state.alertCache[area.ID], alertId, ALERT_CACHE_EXPIRY_MS);
    if (alert && !state.areas[area.ID].alert_ids.includes(alertId)) {
      state.areas[area.ID].alert_ids.push(alertId);
    }
  });
};

const updateAlertCache = (state: Draft<AreasState>, alert: TrailsAlertResponse) => {
  if (alert.type !== AlertModel.AreaAlert) {
    return;
  }

  const areaId = alert.objectId;
  state.alertCache[areaId] ||= {};
  timedRecordSet(state.alertCache[areaId], alert.id, getAlgoliaAlert(alert));
};

export const trailsSlice = createSlice({
  name: 'areas',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchGroupsAreas.pending, state => {
        state.started = true;
        state.loading = true;
        state.areas = {};
        state.error = null;
      })
      .addCase(fetchGroupsAreas.fulfilled, (state, action) => {
        state.loading = false;
        state.areas = {};
        action.payload.forEach(area => {
          state.areas[area.ID] = area;
          addAlertsFromCache(state, area);
        });
      })
      .addCase(fetchGroupsAreas.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      })
      .addCase(createAlert.fulfilled, (state, action) => {
        action.payload.forEach(alert => {
          updateAlertIds(state, alert);
          updateAlertCache(state, alert);
        });
      })
      .addCase(updateAlert.fulfilled, (state, action) => {
        updateAlertIds(state, action.payload);
        updateAlertCache(state, action.payload);
      });
  }
});

export default trailsSlice.reducer;
